import { FormEvent, useEffect, useId, useMemo, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { AnimalDetailModal } from '../../components/modals/AnimalDetailModal';
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { AnimalsSubnav } from '../../components/subnav/AnimalsSubnav';
import { SortHeader } from '../../components/forms/SortHeader';
import { Field } from '../../components/forms/Field';
import { FormModal } from '../../components/modals/FormModal';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import {
  formatDateTime,
  PROGRAM_STATUS_LABELS,
  SEX_LABELS,
  ANIMAL_STATUS_LABELS,
} from "../../lib/format";
import { useFilterFieldProps, passwordFieldProps } from "../../lib/inputPrivacy";
import { useTableControls } from "../../lib/tableControls";
import { loadWithReplica } from "../../lib/offline/helpers";
import { animalsListFromReplica } from "../../lib/offline/sanitize";
import type { Animal } from "../../types";

function archiveValue(animal: Animal, key: string): unknown {
  switch (key) {
    case "tag":
      return animal.tag;
    case "status":
      return animal.status;
    case "archived_at":
      return animal.archived_at;
    case "breed":
      return animal.breed || "";
    case "program_status":
      return animal.program_status;
    case "sex":
      return animal.sex;
    default:
      return null;
  }
}

export function AnimalsArchivePage() {
  const formatTag = useFormatEarTag();
  const fieldNs = useId().replace(/:/g, "");
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteTag, setDeleteTag] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [detailTag, setDetailTag] = useState<string | null>(null);
  const tagFilterProps = useFilterFieldProps("tag");

  const { view, sortKey, sortDir, filters, toggleSort, setFilter } = useTableControls({
    rows: animals,
    getValue: archiveValue,
    defaultSortKey: "archived_at",
    defaultSortDir: "desc",
  });

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    const result = await loadWithReplica<Animal[]>(
      "animals:archived",
      () =>
        api.GET("/api/animals", {
          params: { query: { scope: "archived" } },
        }),
    );
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać archiwum."));
      return;
    }
    setError(null);
    setAnimals(animalsListFromReplica(result.data) as Animal[]);
  }

  async function onDelete(event: FormEvent) {
    event.preventDefault();
    if (!deleteTag || !deletePassword) {
      setError("Podaj hasło administratora.");
      return;
    }
    setBusy(true);
    const deleted = await api.DELETE("/api/animals/{tag}", {
      params: { path: { tag: deleteTag } },
      body: { password: deletePassword },
    });
    setBusy(false);
    if (deleted.error) {
      setError(apiError(deleted.error, "Nie udało się usunąć zwierzęcia."));
      return;
    }
    setDeleteTag(null);
    setDeletePassword("");
    setInfo(`Usunięto zwierzę ${formatTag(deleteTag)}.`);
    await load();
  }

  const breeds = useMemo(() => {
    const set = new Set<string>();
    for (const animal of animals) {
      if (animal.breed) {
        set.add(animal.breed);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b, "pl"));
  }, [animals]);

  return (
    <main className="page" data-testid="animals-archive-page">
      <header className="page__header">
        <p className="page__kicker">Stado</p>
        <h1>Archiwum zwierząt</h1>
      </header>
      <AnimalsSubnav />
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />
      <section className="panel">
        <div className="filters">
          <input
            className="input"
            type="search"
            placeholder="Szukaj kolczyka…"
            value={filters.tag ?? ""}
            onChange={(event) => setFilter("tag", event.target.value)}
            data-testid="archive-search"
            {...tagFilterProps}
          />
          <select
            className="input"
            value={filters.status ?? ""}
            onChange={(event) => setFilter("status", event.target.value)}
            aria-label="Filtr statusu"
            data-testid="archive-status-filter"
          >
            <option value="">Status: wszystkie</option>
            <option value="dead">{ANIMAL_STATUS_LABELS.dead}</option>
            <option value="sold">{ANIMAL_STATUS_LABELS.sold}</option>
            <option value="slaughtered">{ANIMAL_STATUS_LABELS.slaughtered}</option>
          </select>
          <select
            className="input"
            value={filters.breed ?? ""}
            onChange={(event) => setFilter("breed", event.target.value)}
            aria-label="Filtr rasy"
            data-testid="archive-breed-filter"
          >
            <option value="">Rasa: wszystkie</option>
            {breeds.map((breed) => (
              <option key={breed} value={breed}>
                {breed}
              </option>
            ))}
          </select>
        </div>
        <div className="table-wrap">
          <ResponsiveTable className="tbl" data-testid="animals-archive-table">
            <thead>
              <tr>
                <th>
                  <SortHeader
                    label="Kolczyk"
                    sortKey="tag"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Płeć"
                    sortKey="sex"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Archiwizacja"
                    sortKey="archived_at"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Rasa"
                    sortKey="breed"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Program"
                    sortKey="program_status"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr className="tbl__empty-row">
                  <td colSpan={7} className="muted">
                    Brak zwierząt spełniających filtr.
                  </td>
                </tr>
              ) : (
                view.map((animal) => (
                  <tr key={animal.tag}>
                    <td>
                      <button
                        type="button"
                        className="irz-tag-link"
                        data-testid="animal-tag-link"
                        onClick={() => setDetailTag(animal.tag)}
                      >
                        <EarTag value={animal.tag} />
                      </button>
                    </td>
                    <td>{SEX_LABELS[animal.sex] || animal.sex}</td>
                    <td>{ANIMAL_STATUS_LABELS[animal.status] || animal.status}</td>
                    <td>{formatDateTime(animal.archived_at)}</td>
                    <td>{animal.breed || "—"}</td>
                    <td>
                      {PROGRAM_STATUS_LABELS[animal.program_status] || animal.program_status}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm btn--danger"
                          data-testid="animal-archive-delete"
                          onClick={() => {
                            setDeleteTag(animal.tag);
                            setDeletePassword("");
                            setError(null);
                          }}
                        >
                          Usuń trwale
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      </section>
      <FormModal
        open={Boolean(deleteTag)}
        onClose={() => {
          setDeleteTag(null);
          setDeletePassword("");
        }}
        title="Usuń trwale z archiwum"
        testId="animal-archive-delete-form"
        onSubmit={onDelete}
      >
        <p className="muted">
          Trwale usuń zwierzę <strong><EarTag value={deleteTag} /></strong> z bazy. Tej operacji nie można cofnąć.
        </p>
        <Field label="Hasło administratora">
          <input
            className="input"
            type="password"
            required
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            data-testid="animal-archive-delete-password"
            {...passwordFieldProps(`${fieldNs}-archive-delete`)}
          />
        </Field>
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setDeleteTag(null);
              setDeletePassword("");
            }}
          >
            Anuluj
          </button>
          <button className="btn btn--danger" type="submit" disabled={busy}>
            Usuń trwale
          </button>
        </div>
      </FormModal>
      <AnimalDetailModal
        tag={detailTag}
        animals={animals}
        onClose={() => setDetailTag(null)}
        onOpenTag={setDetailTag}
      />
    </main>
  );
}
